import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useAppMutation from 'hooks/useAppMutation';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { BaseErrorResponse } from 'models/_base';
import {
  ApplicationDnsSetting,
  DeleteAgencyDnsSettingPayload,
} from 'models/CustomDomains';
import { AGENCY_DNS_SETTINGS_BASE_QUERY_KEY } from './useFetchAgencyDnsSettings';

// deletion is async: the API keeps reporting the setting until a job removes it
const useDeleteAgencyDnsSetting = () => {
  const { agencyUid } = useAppUser();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { t } = useTranslation();

  const { mutate: deleteDnsSetting, isLoading: isDeleting } = useAppMutation(
    async ({ domain }: DeleteAgencyDnsSettingPayload) => {
      await API.delete<void>(
        `api/internal/agencies/dns-settings/${encodeURIComponent(domain)}`,
      );
    },
    {
      // the modal hides a cancelled row locally, but callers outside it read this query directly and never refetch on their own, so drop the row here too
      onSuccess: (_data, { domain }) => {
        queryClient.setQueryData<ApplicationDnsSetting[]>(
          [AGENCY_DNS_SETTINGS_BASE_QUERY_KEY, agencyUid],
          (currentSettings = []) =>
            currentSettings.filter((setting) => setting.domain !== domain),
        );
        notifySuccess(t('componentCustomDomains.deleteSuccess'));
      },
      onError: (error: BaseErrorResponse) => {
        notifyError(
          getErrorMessage({
            apiErrorCode: error.response?.data?.apiErrorCode,
            apiErrorMessage:
              error.response?.data?.apiErrorMessage ??
              t('componentCustomDomains.deleteError'),
            baseKey: 'componentCustomDomains.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  return { deleteDnsSetting, isDeleting };
};

export default useDeleteAgencyDnsSetting;
